import { useCallback, useContext, useEffect, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import clsx from 'clsx'
import { rem } from 'polished'
import config from 'constants/config'
import { connect } from 'react-redux'
import styled from 'styled-components'
import GeoContext from 'contexts/geoContext'
import { trackClientEvent } from 'analytics/analytics'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import { pushWithRegion } from 'actions/NavigationActions'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useOrdersByDepartureStatus } from 'hooks/useOrdersByDepartureStatus'
import { RESUBSCRIBE_PROMO_CODE_REGIONS } from 'constants/config/region'
import { fetchLESubscriptionsV2, fetchResubscribeModalDismissedAt, fireEmailResubscribePromoCode, updateLESubscriptionsV2, updateResubscribeModalDismissedAt } from 'actions/LESubscriptionsActions'
import Group from 'components/utils/Group'
import HighlightLabel from 'LuxKit/Label/HighlightLabel'
import TextLink from 'LuxKit/TextLink'
import ImageHero from 'components/Common/ImageHero'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'LuxKit/Typography/Heading'
import RadioInput from 'LuxKit/Radio/RadioInput'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import FormatCurrency from 'components/Common/FormatCurrency'
import ModalContent from 'LuxKit/Modal/ModalContent'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import LuxPlusProductPlaceholder from 'luxPlus/components/LuxPlusProductPlaceholder'
import { SubscriptionSource, type SubscriptionPayloadV2 } from 'api/LESubscriptions'
import useExperiment from 'hooks/Experiments/useExperiment'
import { Experiments } from 'constants/experiments'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import LoyaltyTierPill from 'loyalty/components/LoyaltyTierPill'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'

const StyledModalContent = styled(ModalContent)`
  padding: 0;
`

const LuxPlusHeader = styled(Group)`
  padding: ${rem(48)} ${rem(32)};
  background-color: ${(props) => props.theme.palette.product.luxPlus.background};
`

const Header = styled(ImageHero)`
  max-height: ${rem(192)};
`

const Container = styled(Group)`
  padding: ${rem(20)};
`

const RadioContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }
`

const StyledRadioInput = styled(RadioInput)`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  padding: ${rem(20)};

  ${mediaQueryUp.desktop} {
    padding: ${rem(24)} ${rem(20)};
  }
`

const RecommendedRadioInput = styled(RadioInput)`
  position: relative;
  border: 1px solid ${props => props.theme.palette.highlight.primary.darkForeground};
  padding: ${rem(24)} ${rem(20)} ${rem(20)};

  ${mediaQueryUp.desktop} {
    padding: ${rem(24)} ${rem(20)};
  }

  &.is-luxplus {
    border: 1px solid ${props => props.theme.palette.product.luxPlus.background};
  }
`

const StyledLabel = styled(HighlightLabel)`
  position: absolute;
  top: -${rem(12)};
  inset-inline-start: ${rem(20)};
`

const Footer = styled(Group)`
  padding: ${rem(16)} ${rem(24)} ${rem(24)};
`

export type EmailResubscribeModalResult = { submitted: true } | undefined

interface Props {
  userId?: string
  canRedeemLuxPlusBenefits: boolean
}

function EmailResubscribeModal(props: Props) {
  const { userId, canRedeemLuxPlusBenefits } = props
  const intl = useIntl()
  const dispatch = useAppDispatch()

  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const [tier] = useCurrentLoyaltyTier()

  const { variation: loyaltyCommSubBonusVariation } = useExperiment(
    Experiments.loyaltyCommSubBonus,
    { canActivate: hasLoyaltyAccount },
  )
  const loyaltyCommSubBonusVariant = hasLoyaltyAccount ? loyaltyCommSubBonusVariation : undefined

  const { loyaltyPointEarn, subscriptionAction } = useMemo((): {
    loyaltyPointEarn?: number
    subscriptionAction: SubscriptionPayloadV2['action']
  } => {
    if (loyaltyCommSubBonusVariant === '500_points') {
      return { loyaltyPointEarn: 500, subscriptionAction: 'subscribeToCommunications500' }
    }

    if (loyaltyCommSubBonusVariant === '750_points') {
      return { loyaltyPointEarn: 750, subscriptionAction: 'subscribeToCommunications750' }
    }

    return { loyaltyPointEarn: undefined, subscriptionAction: undefined }
  }, [loyaltyCommSubBonusVariant])

  const modalContext = useModalElementContext<EmailResubscribeModalResult>()
  const { currentRegionCode } = useContext(GeoContext)

  const { loading, upcoming, previous } = useOrdersByDepartureStatus()
  const hasNoCancelledEscape = useMemo(() => !!upcoming.length || !!previous.length, [upcoming, previous])
  const showPromoCodeVariant = useMemo(() => hasNoCancelledEscape && RESUBSCRIBE_PROMO_CODE_REGIONS.includes(currentRegionCode), [hasNoCancelledEscape, currentRegionCode])

  const unlockOfferTitle = intl.formatMessage({
    id: 'A987XVyS9DR_KbmlKfV4',
    defaultMessage: 'Unlock this special offer when you subscribe',
    description: 'Heading when promo code variant is shown for email resubscribe modal',
  })

  const subscribeLuxPlusPointsTitle = intl.formatMessage({
    id: 'Q9vP5Dt9_C_z-oGj-Ftz',
    defaultMessage: 'Subscribe today to earn {points} bonus points and get the most out of your LuxPlus+ membership',
    description: 'Heading for LuxPlus members with loyalty points on email resubscribe modal',
  }, { points: loyaltyPointEarn })

  const subscribePointsTitle = intl.formatMessage({
    id: 'oJswkot9-t38W9z2hKTA',
    defaultMessage: 'Subscribe today to earn {points} bonus points and get the most out of Luxury Escapes',
    description: 'Heading for non-LuxPlus users with loyalty points on email resubscribe modal',
  }, { points: loyaltyPointEarn })

  const subscribeLuxPlusTitle = intl.formatMessage({
    id: 'DOIojJcr8nlK0zvQpaS5',
    defaultMessage: 'Subscribe to get the most out of your LuxPlus+ membership',
    description: 'Heading for LuxPlus members without points on email resubscribe modal',
  })

  const subscribeTitle = intl.formatMessage({
    id: 'eYrz72ekU64dXRTqHROt',
    defaultMessage: 'Subscribe today to get the most out of Luxury Escapes',
    description: 'Heading for non-LuxPlus users without points on email resubscribe modal',
  })

  const optionsHeadingTitle = useMemo(() => {
    if (showPromoCodeVariant) return unlockOfferTitle
    if (loyaltyPointEarn) {
      return canRedeemLuxPlusBenefits ? subscribeLuxPlusPointsTitle : subscribePointsTitle
    }
    return canRedeemLuxPlusBenefits ? subscribeLuxPlusTitle : subscribeTitle
  }, [showPromoCodeVariant, canRedeemLuxPlusBenefits, loyaltyPointEarn, unlockOfferTitle, subscribeLuxPlusPointsTitle, subscribePointsTitle, subscribeLuxPlusTitle, subscribeTitle])

  const snackbarMessage = intl.formatMessage({
    id: 'DK9jCJ6WwkSfj9AF2Qsp',
    defaultMessage: 'You’ll now receive personalised offers and recommendations.',
    description: 'Success snackbar message after subscribing to email communications',
  })

  const snackbarHeading = intl.formatMessage({
    id: 'ljXbRHwA_AzokA-0RL_2',
    defaultMessage: 'You’ve successfully subscribed',
    description: 'Success snackbar heading after subscribing to email communications',
  })

  const onClose = useCallback(() => {
    if (!userId) return
    dispatch(updateResubscribeModalDismissedAt(userId))
    modalContext.resolve(undefined)
  }, [dispatch, modalContext, userId])

  const goToPreferences = useCallback(() => {
    dispatch(pushWithRegion(config.header.emailPreferences.to || ''))
    onClose()
  }, [dispatch, onClose])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!userId) return

    const formValues = formToObject<{ subscribeOption: 'basic' | 'full' }>(e.currentTarget)

    const eventSubject = formValues.subscribeOption === 'basic' ? 'personalised' : 'personalised-weekly'
    trackClientEvent({
      subject: canRedeemLuxPlusBenefits ? `${eventSubject}-luxplus` : eventSubject,
      action: showPromoCodeVariant ? 'promo_modal' : '90_day_session_modal',
      type: 'interaction',
      category: 'resubscribe',
      optimizelyEventId: '4869005236764672',
      optimizelyEventKey: 'resubscribe_to_comms',
    })

    const fullSubscription = formValues.subscribeOption === 'full'
    dispatch(updateLESubscriptionsV2({
      userId,
      subscriptions: {
        smsSubscribed: true,
        emailSubscribed: true,
        appPushSubscribed: true,
        myJourneySubscribed: true,
        todaysEscapesSubscribed: true,
        curatedCollectionSubscribed: true,
        todaysEscapesCadence: fullSubscription ? 'DAILY' : 'WEEKLY',
      },
      source: SubscriptionSource.RESUBSCRIBE_MODAL,
      action: subscriptionAction,
    }))

    if (showPromoCodeVariant) {
      dispatch(fireEmailResubscribePromoCode(userId))
    }

    showSnackbar(snackbarMessage, 'success', {
      heading: snackbarHeading,
      icon: <SolidCheckCircleIcon />,
    })
    modalContext.resolve({ submitted: true })
  }

  useEffect(() => {
    if (userId) {
      dispatch(fetchLESubscriptionsV2(userId))
      dispatch(fetchResubscribeModalDismissedAt(userId))
    }
  }, [dispatch, userId])

  useEffect(() => {
    trackClientEvent({
      subject: 'impression',
      action: showPromoCodeVariant ? 'promo_modal' : '90_day_session_modal',
      type: 'nonInteraction',
      category: 'resubscribe',
    })
  }, [showPromoCodeVariant])

  const subscribeMailingListText = intl.formatMessage({
    id: '3sgO7jZjZgNe8nVfUYUI',
    defaultMessage: 'Subscribe to our mailing list and',
    description: 'Heading text above promo offer on email resubscribe modal',
  })

  const dontMissOutPointsText = intl.formatMessage({
    id: 'CRcVRfUFLbDdWzu2akJH',
    defaultMessage: 'Don’t miss out on the latest member only offers and {points} bonus points!',
    description: 'LuxPlus heading with loyalty points on email resubscribe modal',
  }, { points: loyaltyPointEarn })

  const dontMissOutText = intl.formatMessage({
    id: 'dTwCvX7jGQLKYHND0YBj',
    defaultMessage: 'Don’t miss out on the latest members-only benefits and offers',
    description: 'LuxPlus heading without points on email resubscribe modal',
  })

  const termsText = intl.formatMessage({
    id: 'nu2_i_yD6N1nSvCxu84z',
    defaultMessage: 'Terms & Conditions',
    description: 'Terms and conditions link text on email resubscribe modal',
  })

  const applyText = intl.formatMessage({
    id: 'KeqBB9LWZQLHQ81MpHDd',
    defaultMessage: 'apply',
    description: 'Text after terms and conditions link on email resubscribe modal',
  })

  const recommendedLabel = intl.formatMessage({
    id: '77FosKqdetDoieCZ4If4',
    defaultMessage: 'Recommended',
    description: 'Label badge text for recommended subscription option',
  })

  const neverMissLabel = intl.formatMessage({
    id: '6p9DfT50lWL088-diV-S',
    defaultMessage: 'Never miss an offer',
    description: 'Heading for the full subscription radio option on the form',
  })

  const neverMissLuxPlusDesc = intl.formatMessage({
    id: 'VzhhxqPR2ULRX4_kn7cw',
    defaultMessage: 'All your LuxPlus+ benefits, updates, and all our best offers',
    description: 'Description for full subscription option for LuxPlus members',
  })

  const neverMissDesc = intl.formatMessage({
    id: 'Y2l12h7eFSRVUeLiWbPq',
    defaultMessage: 'Enjoy all our top offers delivered straight to you',
    description: 'Description for full subscription option for non-LuxPlus users',
  })

  const justPersonalisedLabel = intl.formatMessage({
    id: 'W5WZ6rsi7fRtWsO8LrSC',
    defaultMessage: 'Just personalised offers',
    description: 'Heading for the basic subscription radio option on the form',
  })

  const justPersonalisedLuxPlusDesc = intl.formatMessage({
    id: 'C6gkVUdsR2L4ulcpDZdN',
    defaultMessage: 'LuxPlus+ benefits, updates, and only offers that match your preferences',
    description: 'Description for basic subscription option for LuxPlus members',
  })

  const justPersonalisedDesc = intl.formatMessage({
    id: 'kuWN84_lw-7uwer03J8v',
    defaultMessage: 'Only receive offers that match your preferences',
    description: 'Description for basic subscription option for non-LuxPlus users',
  })

  const earnPointsLabel = intl.formatMessage({
    id: 'xIcei1k-_naTNMSqJ4i-',
    defaultMessage: 'Earn {points} pts',
    description: 'Loyalty tier pill text showing points earned for subscribing',
  }, { points: loyaltyPointEarn })

  const subscribeLabel = intl.formatMessage({
    id: 'TGgULM4On5I2Q2knGLcX',
    defaultMessage: 'Subscribe',
    description: 'Primary action button text on email resubscribe modal',
  })

  const noThanksLabel = intl.formatMessage({
    id: 'bjcanoihujFyQRtHM1k5',
    defaultMessage: 'No thanks',
    description: 'Dismiss button text on promo variant of email resubscribe modal',
  })

  const managePreferencesLabel = intl.formatMessage({
    id: 'HRfpXfIMTAhlkvqIGewv',
    defaultMessage: 'Manage preferences',
    description: 'Button text to go to email preferences page from resubscribe modal',
  })

  if (!userId) return null

  return (
    <ModalBase onClose={onClose}>
      {loading && <LoadingIndicator floating opaque />}
      <ModalHeader title="" isOverlay={showPromoCodeVariant} dismissible={!showPromoCodeVariant} onCloseButtonClick={onClose} />
      <ModalBody>
        <StyledModalContent>
          <form onSubmit={onSubmit}>
            {showPromoCodeVariant && (
              <Header
                brightness={-10}
                gravity="auto"
                fit="center"
                quality="good"
                mobileAspectRatio="9:4"
                desktopAspectRatio="3:1"
                id="ykvv8uhtmhu5hegw0ejm"
              >
                <Group direction="vertical" gap={12}>
                  <Heading variant="heading6" align="center" colour="dark-contrast">{subscribeMailingListText}</Heading>
                  <Heading variant="heading1" align="center" colour="dark-contrast">
                    <FormattedMessage
                      id="bdPogRbQhqL8BBHTrsja"
                      defaultMessage="Get <italic><currency></currency> off</italic><br></br>your next booking"
                      description="Email resubscribe modal promotional heading with discount amount"
                      values={{
                        currency: () => <FormatCurrency value={50} />,
                        italic: (chunks) => <i>{chunks}</i>,
                        br: () => <br />,
                      }}
                    />
                  </Heading>
                </Group>
              </Header>
            )}
            {!showPromoCodeVariant && canRedeemLuxPlusBenefits && (
              <LuxPlusHeader direction="vertical" gap={12} horizontalAlign="center">
                <LuxPlusProductPlaceholder />
                {
                  !!loyaltyPointEarn &&
                  <Heading variant="heading3" colour="dark-contrast" align="center">{dontMissOutPointsText}</Heading>
                }
                {
                  !loyaltyPointEarn &&
                  <Heading variant="heading3" colour="dark-contrast" align="center">{dontMissOutText}</Heading>
                }
              </LuxPlusHeader>
            )}
            {!showPromoCodeVariant && !canRedeemLuxPlusBenefits && (
              <Header
                brightness={-10}
                gravity="auto"
                fit="center"
                quality="good"
                mobileAspectRatio="9:4"
                desktopAspectRatio="3:1"
                id="cjibv4atdp6qownlqz2b"
              >
                <Heading variant="heading1" align="center" colour="dark-contrast">
                  <FormattedMessage
                    id="rKYk-4wGQtVPTNNngs1P"
                    defaultMessage="<italic>Our best offers</italic><br></br>delivered to you"
                    description="Heading for non-LuxPlus users on email resubscribe modal"
                    values={{
                      italic: (chunks) => <i>{chunks}</i>,
                      br: () => <br />,
                    }}
                  />
                </Heading>
              </Header>
            )}
            <Container direction="vertical" gap={32}>
              <Group direction="vertical" gap={8}>
                <Heading variant="heading4">{optionsHeadingTitle}</Heading>
                {showPromoCodeVariant && (<BodyText variant="small"><TextLink to={config.contentLinks.termsAndConditions.to} target="_blank" weight="regular">{termsText}</TextLink> {applyText}</BodyText>)}
              </Group>
              <RadioContainer>
                <RecommendedRadioInput className={clsx({ 'is-luxplus': canRedeemLuxPlusBenefits })} name="subscribeOption" value="full" defaultChecked>
                  <>
                    <StyledLabel>
                      {recommendedLabel}
                    </StyledLabel>
                    {canRedeemLuxPlusBenefits ? <><strong>{neverMissLabel}</strong><br />{neverMissLuxPlusDesc}</> : <><strong>{neverMissLabel}</strong><br />{neverMissDesc}</>}
                  </>
                </RecommendedRadioInput>
                <StyledRadioInput name="subscribeOption" value="basic">
                  {canRedeemLuxPlusBenefits ? <><strong>{justPersonalisedLabel}</strong><br />{justPersonalisedLuxPlusDesc}</> : <><strong>{justPersonalisedLabel}</strong><br />{justPersonalisedDesc}</>}
                </StyledRadioInput>
              </RadioContainer>
              {!!loyaltyPointEarn &&
                <LoyaltyTierPill kind="primary" tier={tier}>{earnPointsLabel}</LoyaltyTierPill>}
            </Container>
            <Footer direction="vertical" gap={8}>
              <TextButton kind="primary" type="submit">{subscribeLabel}</TextButton>
              {showPromoCodeVariant && <TextButton kind="tertiary" onClick={onClose}>{noThanksLabel}</TextButton>}
              {!showPromoCodeVariant && <TextButton kind="tertiary" onClick={goToPreferences}>{managePreferencesLabel}</TextButton>}
            </Footer>
          </form>
        </StyledModalContent>
      </ModalBody>
    </ModalBase>
  )
}

const mapStateToProps = (state: App.State) => ({
  userId: state.auth.account.memberId,
  canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(state),
})

export default connect(mapStateToProps)(EmailResubscribeModal)
